import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, BookMarked, Download, Ruler, ImageOff } from "lucide-react";

const TRIM_DIMS = { "5x8": [5, 8], "5.25x8": [5.25, 8], "5.5x8.5": [5.5, 8.5], "6x9": [6, 9] };
const PAPER_THICK = { cream: 0.0025, white: 0.002252, groundwood: 0.00235 };
const HC_TRIMS = ["5.5x8.5", "6x9"];
const HC = { wrap: 0.591, hinge: 0.197, boardH: 0.118, spineBoard: 0.189, minPages: 75 };

async function errDetail(e, fallback) {
  const d = e?.response?.data;
  try {
    if (d instanceof Blob) { const j = JSON.parse(await d.text()); return j.detail || fallback; }
  } catch { /* ignore */ }
  return (typeof d?.detail === "string" ? d.detail : fallback);
}

export default function WrapCoverDialog({ open, onOpenChange, project }) {
  const [binding, setBinding] = useState("paperback");
  const [trim, setTrim] = useState("6x9");
  const [paper, setPaper] = useState("cream");
  const [pages, setPages] = useState("");
  const [blurb, setBlurb] = useState("");
  const [info, setInfo] = useState(null);
  const [infoError, setInfoError] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const isHc = binding === "hardcover";
  const minPages = isHc ? HC.minPages : 24;

  const openRef = React.useRef(open);
  openRef.current = open;
  const fetchInfo = useCallback((t, pp, b, retry = false) => {
    if (!project?.id) return;
    setLoading(true);
    setInfoError("");
    api.get(`/projects/${project.id}/cover/wrap/info`, { params: { trim: t, paper: pp, binding: b, retry }, timeout: 60000 })
      .then(({ data }) => {
        setInfo(data);
        setPages(data.page_count ? String(data.page_count) : "");
        setBlurb((prev) => (prev ? prev : (data.default_blurb || "")));
        if (data.counting) {
          setTimeout(() => { if (openRef.current) fetchInfo(t, pp, b); }, 3000);
        } else {
          setLoading(false);
        }
      })
      .catch((e) => {
        setInfo(null);
        setLoading(false);
        const d = e?.response?.data?.detail;
        setInfoError(typeof d === "string" ? d : (e?.response?.status ? `The server couldn't measure your book (error ${e.response.status}).` : "The server took too long to measure your book."));
      });
  }, [project?.id]);

  useEffect(() => { if (open) { setBlurb(""); fetchInfo(trim, paper, binding); } }, [open]); // eslint-disable-line
  const onTrim = (t) => { setTrim(t); fetchInfo(t, paper, binding); };
  const onBinding = (b) => {
    setBinding(b);
    let t = trim;
    if (b === "hardcover" && !HC_TRIMS.includes(trim)) { t = "6x9"; setTrim(t); }
    let pp = paper;
    if (b === "hardcover" && paper === "groundwood") { pp = "cream"; setPaper(pp); }
    fetchInfo(t, pp, b);
  };

  const [tw, th] = TRIM_DIMS[trim];
  const p = Number(pages) || 0;
  let spine, fullW, fullH;
  if (isHc) {
    spine = p * PAPER_THICK[paper] + HC.spineBoard;
    fullW = (2 * HC.wrap + 2 * tw + 2 * HC.hinge + spine).toFixed(2);
    fullH = (th + 2 * (HC.wrap + HC.boardH)).toFixed(2);
  } else {
    spine = p * PAPER_THICK[paper];
    fullW = (2 * tw + spine + 0.25).toFixed(2);
    fullH = (th + 0.25).toFixed(2);
  }
  const spineTextOk = p >= 100;
  const trimOptions = isHc ? HC_TRIMS : Object.keys(TRIM_DIMS);
  const pagesTooLow = p > 0 && p < minPages;
  const maxPages = isHc ? 550 : 828;
  const pagesTooHigh = p > maxPages;

  const download = async () => {
    setBusy(true);
    try {
      const base = `/projects/${project.id}/cover/wrap`;
      const { data: start } = await api.post(`${base}/start`, { trim, paper, pages: Number(pages) || undefined, blurb, binding });
      const t0 = Date.now();
      for (;;) {
        await new Promise((r) => setTimeout(r, 3000));
        let st;
        try { ({ data: st } = await api.get(`${base}/jobs/${start.job_id}`)); } catch { if (Date.now() - t0 > 600000) throw new Error("timeout"); continue; }
        if (st.status === "done") break;
        if (st.status === "error") throw new Error(st.error || "Couldn't build the cover — please try again.");
        if (Date.now() - t0 > 600000) throw new Error("The cover is taking too long — please try again.");
      }
      const res = await api.get(`${base}/jobs/${start.job_id}/download`, { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const safe = (project.title || "book").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "") || "book";
      a.download = `${safe}_${isHc ? "Hardcover" : "Wrap"}_Cover_${trim}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`${isHc ? "Hardcover" : "Wraparound"} cover downloaded`);
    } catch (e) {
      toast.error(e?.response ? await errDetail(e, "Couldn't build the cover — please try again.") : (e?.message || "Couldn't build the cover — please try again."));
    } finally { setBusy(false); }
  };

  const noCover = info && info.has_cover === false;
  const TRIM_LABELS = { "5x8": "5 × 8 in", "5.25x8": "5.25 × 8 in", "5.5x8.5": "5.5 × 8.5 in", "6x9": "6 × 9 in" };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="bg-popover max-w-lg" data-testid="wrap-cover-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <BookMarked className="w-5 h-5 text-primary" /> {isHc ? "Hardcover (case laminate)" : "Wraparound paperback cover"}
          </DialogTitle>
          <DialogDescription>
            A single print-ready PDF — back cover + spine + front — sized to KDP spec. The spine width is set from your page count.
          </DialogDescription>
        </DialogHeader>

        {loading && !info ? (
          <div className="flex flex-col items-center gap-2 py-12" data-testid="wrap-loading">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            <p className="text-xs text-muted-foreground">Measuring your book for the spine… long books can take up to a minute.</p>
          </div>
        ) : infoError && !info ? (
          <div className="py-8 text-center space-y-3" data-testid="wrap-info-error">
            <p className="text-sm text-muted-foreground max-w-sm mx-auto">{infoError}</p>
            <Button variant="outline" size="sm" onClick={() => fetchInfo(trim, paper, binding)} data-testid="wrap-info-retry">Try again</Button>
          </div>
        ) : noCover ? (
          <div className="py-8 text-center space-y-3" data-testid="wrap-no-cover">
            <ImageOff className="w-8 h-8 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground max-w-xs mx-auto">
              Add a front cover to this book first (upload one or generate it with AI), then come back to build the full wraparound cover.
            </p>
          </div>
        ) : (
          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label>Binding</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="wrap-binding-toggle">
                {[["paperback", "Paperback"], ["hardcover", "Hardback"]].map(([v, lbl]) => (
                  <button key={v} type="button" onClick={() => onBinding(v)} data-testid={`wrap-binding-${v}`}
                    className={`h-9 rounded-md border text-sm font-medium transition-colors ${binding === v ? "border-primary bg-primary/10 text-primary" : "border-input bg-background text-muted-foreground hover:text-foreground"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
              {isHc && (
                <p className="text-[11px] text-muted-foreground">Hardback (case laminate) — KDP offers it in 5.5 × 8.5 and 6 × 9 in, 75+ pages.</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Trim size</Label>
                <select value={trim} onChange={(e) => onTrim(e.target.value)} data-testid="wrap-trim-select"
                  className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm">
                  {trimOptions.map((t) => (<option key={t} value={t}>{TRIM_LABELS[t]}</option>))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Paper</Label>
                <select value={paper} onChange={(e) => { setPaper(e.target.value); fetchInfo(trim, e.target.value, binding); }} data-testid="wrap-paper-select"
                  className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm">
                  <option value="cream">Cream (novels)</option>
                  <option value="white">White</option>
                  {!isHc && <option value="groundwood">Groundwood (paperback only)</option>}
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="flex items-center justify-between">
                <span>Final print PDF page count</span>
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" /> : null}
              </Label>
              <div className="h-10 rounded-md border border-input bg-secondary/30 px-3 flex items-center text-sm font-semibold" data-testid="wrap-page-count">
                {info?.page_count ? `${info.page_count} pages` : info?.count_error ? "—" : "Measuring the print PDF…"}
              </div>
              {info?.count_error ? (
                <p className="text-[11px] text-destructive flex items-center gap-2" data-testid="wrap-count-error">
                  {info.count_error}
                  <button type="button" className="underline" onClick={() => fetchInfo(trim, paper, binding, true)} data-testid="wrap-count-retry">Try again</button>
                </p>
              ) : (
                <p className="text-[11px] text-muted-foreground">Measured from the actual {TRIM_LABELS[trim]} print interior (same file as “Download interior”). Long books can take a minute or two.</p>
              )}
              {pagesTooLow && (
                <p className="text-[11px] text-amber-500" data-testid="wrap-pages-warning">
                  {isHc
                    ? `KDP hardcovers need at least ${HC.minPages} pages — add more content before downloading.`
                    : "KDP paperbacks need at least 24 pages — raise this before downloading."}
                </p>
              )}
              {pagesTooHigh && (
                <p className="text-[11px] text-amber-500" data-testid="wrap-pages-too-high">
                  KDP prints {isHc ? "hardbacks" : "paperbacks"} up to about {maxPages} pages — at {p} pages KDP will reject this book. Consider splitting it into two volumes. You can still download the cover to check the design.
                </p>
              )}
            </div>

            <div className="rounded-lg border border-border bg-secondary/30 p-3 text-xs space-y-1" data-testid="wrap-specs">
              <div className="flex items-center gap-1.5 font-semibold text-primary"><Ruler className="w-3.5 h-3.5" /> Cover specs</div>
              <div>Spine width: <span className="font-medium">{spine.toFixed(3)} in</span>{isHc ? <span className="text-muted-foreground"> (includes board allowance)</span> : null}</div>
              <div>Full cover ({isHc ? "with wrap + bleed" : "with bleed"}): <span className="font-medium">{fullW} × {fullH} in</span></div>
              <div className="text-muted-foreground">{spineTextOk ? "Spine title + author will be printed (100+ pages)." : "No spine text — KDP requires 100+ pages for a printed spine."}</div>
              {isHc && <div className="text-muted-foreground">Hinge channels + board wrap are kept clear of text automatically.</div>}
            </div>

            <div className="space-y-1.5">
              <Label>Back-cover blurb</Label>
              <Textarea value={blurb} onChange={(e) => setBlurb(e.target.value)} rows={5} data-testid="wrap-blurb"
                placeholder="Your back-cover description…" />
              <p className="text-[11px] text-muted-foreground">We keep the bottom-right of the back cover clear for Amazon’s barcode.</p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Close</Button>
          {!noCover && !(infoError && !info) && (
            <Button onClick={download} disabled={busy || loading || pagesTooLow || !info?.page_count} data-testid="wrap-download" className="gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download {isHc ? "hardcover" : "wraparound"} cover
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
